import React from 'react';
import { AlertCircle } from 'lucide-react';

interface ItemErrorBoundaryProps {
  children: React.ReactNode;
  /** Used only for logging so a broken card can be traced back to its row. */
  itemId?: string;
}

interface ItemErrorBoundaryState {
  hasError: boolean;
}

/**
 * Per-card error boundary.
 *
 * One card with bad content (for example a synced X bookmark with no `url`)
 * used to throw during render and take the whole canvas down to the global
 * "Something went wrong" screen. Wrapping each rendered item in this boundary
 * keeps the failure local: the broken card shows a compact placeholder and the
 * rest of the board keeps working.
 */
export class ItemErrorBoundary extends React.Component<
  ItemErrorBoundaryProps,
  ItemErrorBoundaryState
> {
  state: ItemErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ItemErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(
      `[ItemErrorBoundary] Item ${this.props.itemId ?? '(unknown)'} failed to render:`,
      error,
      errorInfo
    );
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          data-testid="item-error-placeholder"
          className="w-full h-full rounded-lg bg-muted/30 border border-dashed border-border flex flex-col items-center justify-center gap-2 p-4 text-center"
        >
          <AlertCircle className="w-6 h-6 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Couldn't display this item</span>
        </div>
      );
    }

    return this.props.children;
  }
}
